import { AudioPlayer } from "./AudioPlayer";
import { Download, FileText, Image as ImageIcon, Video } from "lucide-react";

interface MessageFile {
  type?: string;
  url?: string;
  name?: string;
  size?: number;
  mimeType?: string;
  duration?: number;
}

interface MixedMessageProps {
  content: string;
  files: (MessageFile | string)[];
  isOwnMessage?: boolean;
}

export function MixedMessage({
  content,
  files,
  isOwnMessage = true,
}: MixedMessageProps) {
  // Função para verificar se um arquivo é áudio pela extensão
  const isAudioFile = (filename: string) => {
    const audioExtensions = [".webm", ".mp3", ".wav", ".ogg", ".m4a", ".aac"];
    const extension = filename
      .toLowerCase()
      .substring(filename.lastIndexOf("."));
    return audioExtensions.includes(extension);
  };

  // Função para extrair nome do arquivo da URL
  const getFilenameFromUrl = (url: string) => {
    try {
      const urlParts = url.split("/");
      return urlParts[urlParts.length - 1];
    } catch {
      return "";
    }
  };

  // Separar arquivos de áudio dos outros
  const audioFiles = files.filter((file) => {
    if (typeof file === "string") {
      return isAudioFile(getFilenameFromUrl(file));
    }
    if (file.type === "audio") return true;
    const filename = file.name || getFilenameFromUrl(file.url || "");
    return isAudioFile(filename);
  });

  const otherFiles = files.filter((file) => {
    if (typeof file === "string") {
      return !isAudioFile(getFilenameFromUrl(file));
    }
    if (file.type === "audio") return false;
    const filename = file.name || getFilenameFromUrl(file.url || "");
    return !isAudioFile(filename);
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) {
      return <ImageIcon size={20} className="text-green-500" />;
    }
    if (mimeType.startsWith("video/")) {
      return <Video size={20} className="text-purple-500" />;
    }
    return <FileText size={20} className="text-azul-400" />;
  };

  const downloadFile = (fileData: MessageFile | string) => {
    const link = document.createElement("a");

    if (typeof fileData === "string") {
      link.href = fileData;
      link.download = getFilenameFromUrl(fileData);
    } else {
      link.href = fileData.url || "";
      link.download = fileData.name || "file";
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para obter dados normalizados do arquivo
  const getFileData = (file: MessageFile | string) => {
    if (typeof file === "string") {
      return {
        url: file,
        name: getFilenameFromUrl(file),
        mimeType: "",
        size: 0,
      };
    }

    // Se já existe url válida, nunca sobrescrever
    let url = file.url;
    // Só reconstruir a URL se não houver url e o nome for um nome de arquivo com sufixo/ID
    if (!url || url === "") {
      if (file.name && file.name.match(/^https?:\/\//i)) {
        url = file.name;
      } else if (
        file.name &&
        file.name.match(
          /_\d+\.(jpg|jpeg|png|gif|bmp|webp|pdf|docx?|xlsx?|pptx?)$/i,
        )
      ) {
        url = `https://storage.googleapis.com/addesksfiles/chats/${file.name}`;
      } else {
        url = file.url || "";
      }
    }

    return {
      url,
      name: file.name || getFilenameFromUrl(file.url || ""),
      mimeType: file.mimeType || "",
      size: file.size || 0,
    };
  };
  // Modificação para não reconstruir a URL se o nome já não for igual ao nome original do arquivo
  // Usar sempre file.url se existir, e só reconstruir se não houver url e o nome for um nome de arquivo completo (com sufixo/ID)

  return (
    <div className="space-y-3">
      {/* Texto da mensagem */}

      {/* Áudios */}
      {audioFiles.length > 0 && (
        <div className="space-y-2">
          {audioFiles.map((file, index) => (
            <AudioPlayer
              key={index}
              files={[file]}
              isOwnMessage={isOwnMessage}
            />
          ))}
        </div>
      )}

      {/* Outros arquivos */}
      {otherFiles.length > 0 && (
        <div className="space-y-2">
          {otherFiles.map((file, index) => {
            const fileData = getFileData(file);

            // Se for imagem, mostrar preview (detecta por mimeType ou extensão)
            const imageExtensions = [
              ".jpg",
              ".jpeg",
              ".png",
              ".gif",
              ".bmp",
              ".webp",
            ];
            const isImage =
              fileData.mimeType.startsWith("image/") ||
              imageExtensions.some((ext) =>
                fileData.name.toLowerCase().endsWith(ext),
              );

            // Se for vídeo, mostrar player (detecta por mimeType ou extensão)
            const videoExtensions = [
              ".mp4",
              ".webm",
              ".ogg",
              ".mov",
              ".avi",
              ".mkv",
            ];
            const isVideo =
              fileData.mimeType.startsWith("video/") ||
              videoExtensions.some((ext) =>
                fileData.name.toLowerCase().endsWith(ext),
              );

            if (isVideo) {
              return (
                <div key={fileData.url || index} className="relative group">
                  <video
                    src={fileData.url}
                    controls
                    className="rounded-lg"
                    preload="metadata"
                  >
                    Seu navegador não suporta a reprodução de vídeos.
                  </video>
                  <button
                    onClick={() => downloadFile(file)}
                    className="absolute top-3 right-3 p-2 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-opacity opacity-0 group-hover:opacity-100"
                    title="Download"
                  >
                    <Download size={16} />
                  </button>
                </div>
              );
            }

            if (isImage) {
              return (
                <div key={fileData.url} className="relative group">
                  <img
                    src={fileData.url}
                    alt={fileData.name}
                    className="max-w-64 rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => window.open(fileData.url, "_blank")}
                  />
                  <button
                    onClick={() => downloadFile(file)}
                    className="absolute top-3 right-3 p-2 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-opacity opacity-0 group-hover:opacity-100"
                    title="Download"
                  >
                    <Download size={16} />
                  </button>
                </div>
              );
            }

            // Outros tipos de arquivo
            return (
              <div
                key={index}
                className="flex items-center gap-3 rounded-xl cursor-pointer group"
                onClick={() => downloadFile(file)}
              >
                <div className="p-1 bg-gray-50 rounded-lg group-hover:bg-azul-50 transition-colors">
                  {getFileIcon(fileData.mimeType)}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium  ${
                      isOwnMessage ? "text-white" : "text-gray-600"
                    } truncate max-w-32`}
                    title={fileData.name}
                  >
                    {fileData.name}
                  </p>
                  <p
                    className={`text-xs  ${
                      isOwnMessage ? "text-gray-100" : "text-gray-600"
                    }`}
                  >
                    {fileData.size > 0
                      ? formatFileSize(fileData.size)
                      : "Arquivo"}
                  </p>
                </div>
                <div className="p-2 text-gray-50 group-hover:text-azul-500 transition-colors">
                  <Download size={18} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {content && (
        <div
          className={`text-sm  ${
            isOwnMessage ? "text-gray-100" : "text-gray-600"
          } leading-relaxed`}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {content}
        </div>
      )}
    </div>
  );
}
